import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import { Fragment, useState } from 'react';
import { z } from 'zod';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { cn } from '@apps/web/lib/utils';
import { SandboxTelemetryDashboard } from '@apps/web/routes/_app/admin/sandbox/telemetry_dashboard';
import {
    type AdminSandboxCallSort,
    useAdminSandboxCalls,
    useAdminSandboxFilePreview,
    useAdminSandboxOrganization,
    useAdminSandboxTelemetry,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type ApiResponseType } from '@libraries/typescript/api_client';

const TELEMETRY_WINDOW_DAYS = [1, 7, 30] as const;

const orgSandboxSearchSchema = z.object({
    days: z.union([z.literal(1), z.literal(7), z.literal(30)]).default(7),
});

export const Route = createFileRoute('/_app/admin/sandbox/$organizationId')({
    head: () => ({ meta: [{ title: 'Sandbox details | Phoebe Admin' }] }),
    component: AdminSandboxOrganizationPage,
    validateSearch: orgSandboxSearchSchema,
});

type SelectedFile = {
    readonly runId: string;
    readonly path: string;
};

type SandboxCall = ApiResponseType<
    '/admin/sandbox/{organization_id}/calls',
    'get'
>['calls'][number];

type ResourceSummary = NonNullable<SandboxCall['resource_summary']>;

function formatBytes(bytes: number | null | undefined): string {
    if (bytes === null || bytes === undefined) {
        return '—';
    }
    if (bytes === 0) {
        return '0 B';
    }
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KiB`;
    }
    if (bytes < 1024 * 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
    }
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
}

function formatDate(value: string | null): string {
    return value ? new Date(value).toLocaleString() : 'unknown';
}

function formatSeconds(seconds: number | null | undefined): string {
    if (seconds === null || seconds === undefined) {
        return '—';
    }
    if (seconds < 1) {
        return `${(seconds * 1000).toFixed(0)} ms`;
    }
    return `${seconds.toFixed(seconds >= 10 ? 0 : 1)} s`;
}

function formatDuration(ms: number | null | undefined): string {
    if (ms === null || ms === undefined) {
        return '—';
    }
    if (ms < 1000) {
        return `${Math.round(ms)} ms`;
    }
    if (ms < 60_000) {
        return `${(ms / 1000).toFixed(1)} s`;
    }
    return `${(ms / 60_000).toFixed(1)} min`;
}

function formatCount(count: number | null | undefined): string {
    if (count === null || count === undefined) {
        return '—';
    }
    return count.toLocaleString();
}

function formatCpuMax(summary: ResourceSummary): string {
    if (
        summary.cpu_max_quota_s === null ||
        summary.cpu_max_period_s === null ||
        summary.cpu_max_period_s === 0
    ) {
        return 'unbounded';
    }
    const cores = summary.cpu_max_quota_s / summary.cpu_max_period_s;
    return `${cores.toFixed(2)} cores`;
}

const CLASSIFICATION_BADGE_TONE: Record<string, string> = {
    clean: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    'nonzero-exit': 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    containment_timeout:
        'bg-orange-500/15 text-orange-700 dark:text-orange-400',
    'oom-kill': 'bg-destructive/15 text-destructive',
    'trusted-kill': 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
};

const CLASSIFICATION_LABEL: Record<string, string> = {
    clean: 'Clean',
    'nonzero-exit': 'Non-zero exit',
    containment_timeout: 'Timed out',
    'oom-kill': 'OOM kill',
    'trusted-kill': 'CPU/PID kill',
};

function ClassificationBadge({
    classification,
}: {
    readonly classification: string | null | undefined;
}) {
    if (!classification) {
        return (
            <Badge variant="outline" className="font-normal">
                No classification
            </Badge>
        );
    }
    return (
        <span
            className={cn(
                'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium',
                CLASSIFICATION_BADGE_TONE[classification] ??
                    'bg-muted text-muted-foreground',
            )}
        >
            {CLASSIFICATION_LABEL[classification] ?? classification}
        </span>
    );
}

function ResourceStat({
    label,
    value,
    hint,
}: {
    readonly label: string;
    readonly value: string;
    readonly hint?: string;
}) {
    return (
        <div className="min-w-24">
            <div className="text-muted-foreground text-xs">{label}</div>
            <div className="font-medium tabular-nums">{value}</div>
            {hint ? (
                <div className="text-muted-foreground text-xs">{hint}</div>
            ) : null}
        </div>
    );
}

const CALL_COST_FORMATTER = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
});

function formatCallCost(
    cost: number | null | undefined,
    status: SandboxCall['cost_status'],
): string {
    if (status === 'pending') {
        return 'pending';
    }
    if (status === 'unavailable') {
        return 'cost unavailable';
    }
    if (cost === null || cost === undefined) {
        return 'cost unavailable';
    }
    return CALL_COST_FORMATTER.format(cost);
}

function CallCard({ call }: { readonly call: SandboxCall }) {
    const summary = call.resource_summary;
    const memoryUsage =
        summary?.memory_peak_b !== undefined &&
        summary.memory_peak_b !== null &&
        summary.memory_max_b !== undefined &&
        summary.memory_max_b !== null &&
        summary.memory_max_b > 0
            ? `${((summary.memory_peak_b / summary.memory_max_b) * 100).toFixed(
                  0,
              )}% of cap`
            : undefined;
    const reservationHint =
        call.reserved_cpu !== null &&
        call.reserved_cpu !== undefined &&
        call.reserved_memory_mib !== null &&
        call.reserved_memory_mib !== undefined
            ? `${call.reserved_cpu} CPU · ${call.reserved_memory_mib} MiB reserved`
            : 'reservation unknown';
    return (
        <div
            className="flex flex-col gap-3 rounded-md border p-3"
            id={`call-${call.event_id}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <ClassificationBadge
                            classification={summary?.exit_classification}
                        />
                        <Badge variant="outline" className="font-normal">
                            {call.mode}
                        </Badge>
                        {call.timed_out ? (
                            <Badge
                                variant="outline"
                                className="border-orange-500/60 text-orange-700 dark:text-orange-400"
                            >
                                Timed out
                            </Badge>
                        ) : null}
                        {summary?.resource_limits_degraded ? (
                            <Badge
                                variant="outline"
                                className="border-amber-500/60 text-amber-700 dark:text-amber-400"
                            >
                                cgroup degraded
                            </Badge>
                        ) : null}
                        {summary?.recycled ? (
                            <Badge variant="outline" className="font-normal">
                                Recycled sandbox
                            </Badge>
                        ) : null}
                    </div>
                    <div className="text-muted-foreground text-xs">
                        {formatDate(call.created_at)} · exit{' '}
                        {call.exit_code ?? '—'}
                        {call.tool_call_id
                            ? ` · tool call ${call.tool_call_id}`
                            : ''}
                    </div>
                    {call.command_hash ? (
                        <div className="text-muted-foreground font-mono text-xs">
                            cmd {call.command_hash.slice(0, 12)}
                        </div>
                    ) : null}
                </div>
                <a
                    className="text-sm underline-offset-4 hover:underline"
                    href={call.trace_url}
                >
                    View trace →
                </a>
            </div>
            {summary ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                    <ResourceStat
                        label="Modal billed cost"
                        value={formatCallCost(call.cost_usd, call.cost_status)}
                        hint={
                            call.cost_status === 'available'
                                ? 'allocated from Modal hourly billing'
                                : call.cost_status === 'pending'
                                  ? 'waiting for Modal billing reconciliation'
                                  : reservationHint
                        }
                    />
                    <ResourceStat
                        label="Memory peak"
                        value={formatBytes(summary.memory_peak_b)}
                        hint={
                            summary.memory_max_b !== null
                                ? `cap ${formatBytes(summary.memory_max_b)}${
                                      memoryUsage ? ` · ${memoryUsage}` : ''
                                  }`
                                : 'cap unknown'
                        }
                    />
                    <ResourceStat
                        label="CPU time"
                        value={formatSeconds(summary.cpu_time_s)}
                        hint={`cap ${formatCpuMax(summary)}`}
                    />
                    <ResourceStat
                        label="PIDs peak"
                        value={formatCount(summary.pids_peak)}
                        hint={
                            summary.pids_max === null
                                ? 'cap unknown'
                                : `cap ${formatCount(summary.pids_max)}`
                        }
                    />
                    <ResourceStat
                        label="Duration"
                        value={formatDuration(summary.duration_ms)}
                    />
                    <ResourceStat
                        label="Telemetry"
                        value={
                            summary.resource_telemetry_failed
                                ? 'read failed'
                                : summary.resource_limits_degraded
                                  ? 'degraded'
                                  : 'ok'
                        }
                    />
                </div>
            ) : (
                <p className="text-muted-foreground text-xs italic">
                    No resource summary — this event was recorded before #13915.
                </p>
            )}
        </div>
    );
}

function AdminSandboxOrganizationPage() {
    const navigate = Route.useNavigate();
    const { organizationId } = Route.useParams();
    const { days } = Route.useSearch();
    const { data, error, isLoading } =
        useAdminSandboxOrganization(organizationId);
    const {
        data: telemetry,
        isLoading: isTelemetryLoading,
        hasRequestFailed: hasTelemetryRequestFailed,
    } = useAdminSandboxTelemetry({ days, organizationId });
    const [callsSort, setCallsSort] =
        useState<AdminSandboxCallSort>('created_at');
    const { data: callsResponse, isLoading: isCallsLoading } =
        useAdminSandboxCalls({ organizationId, days, sort: callsSort });
    const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
    const { data: preview, isLoading: isPreviewLoading } =
        useAdminSandboxFilePreview({
            organizationId,
            runId: selectedFile?.runId ?? null,
            path: selectedFile?.path ?? null,
        });

    if (isLoading) {
        return <p className="text-muted-foreground p-6 text-sm">Loading…</p>;
    }
    if (error || !data) {
        return (
            <div className="p-6">
                <p className="text-destructive text-sm">
                    Unable to load this sandbox.
                </p>
            </div>
        );
    }

    const apiUrl = getRuntimeConfig().apiUrl;

    return (
        <div className="flex flex-col gap-4 p-4 md:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <Link
                        className="text-muted-foreground text-sm underline-offset-4 hover:underline"
                        to="/admin/sandbox"
                        search={{ days }}
                    >
                        ← Sandbox explorer
                    </Link>
                    <h1 className="mt-2 text-xl font-semibold">
                        {data.organization.organization_name}
                    </h1>
                    <p className="text-muted-foreground font-mono text-xs">
                        {data.organization.organization_id}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Badge
                        variant={
                            data.organization.state === 'live'
                                ? 'success'
                                : 'secondary'
                        }
                    >
                        {data.organization.state}
                    </Badge>
                    <div
                        className="flex items-center gap-1"
                        role="group"
                        aria-label="Telemetry window"
                    >
                        {TELEMETRY_WINDOW_DAYS.map((windowDays) => (
                            <Button
                                key={windowDays}
                                size="sm"
                                variant={
                                    days === windowDays ? 'default' : 'outline'
                                }
                                aria-pressed={days === windowDays}
                                onClick={() => {
                                    void navigate({
                                        search: (current) => ({
                                            ...current,
                                            days: windowDays,
                                        }),
                                    });
                                }}
                            >
                                {windowDays === 1 ? '24 h' : `${windowDays} d`}
                            </Button>
                        ))}
                    </div>
                </div>
            </div>
            <SandboxTelemetryDashboard
                data={telemetry}
                isLoading={isTelemetryLoading}
                hasRequestFailed={hasTelemetryRequestFailed}
            />
            <Card>
                <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-1">
                        <CardTitle>Recent sandbox tool calls</CardTitle>
                        <p className="text-muted-foreground text-sm">
                            Each row shows the resource summary the supervisor
                            emitted after the command completed, plus the
                            per-command Modal billed cost so an outlier alert
                            can be traced back to the responsible command.
                        </p>
                    </div>
                    <div
                        className="flex items-center gap-2"
                        role="group"
                        aria-label="Sort recent calls by"
                    >
                        {(
                            [
                                { key: 'created_at', label: 'Newest' },
                                { key: 'cost_usd', label: 'Highest cost' },
                                {
                                    key: 'duration_ms',
                                    label: 'Longest duration',
                                },
                            ] as const
                        ).map((option) => (
                            <Button
                                key={option.key}
                                type="button"
                                variant={
                                    callsSort === option.key
                                        ? 'default'
                                        : 'outline'
                                }
                                size="sm"
                                onClick={() => setCallsSort(option.key)}
                            >
                                {option.label}
                            </Button>
                        ))}
                    </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                    {isCallsLoading ? (
                        <p className="text-muted-foreground text-sm">
                            Loading tool calls…
                        </p>
                    ) : callsResponse?.calls.length ? (
                        <Fragment>
                            {callsResponse.calls.map((call) => (
                                <CallCard key={call.event_id} call={call} />
                            ))}
                            {callsResponse.truncated ? (
                                <p className="text-muted-foreground text-xs">
                                    Showing the first {callsResponse.limit}{' '}
                                    calls. Older calls stay in the run event
                                    log.
                                </p>
                            ) : null}
                        </Fragment>
                    ) : (
                        <p className="text-muted-foreground text-sm">
                            No sandbox tool calls recorded yet for this
                            organization.
                        </p>
                    )}
                </CardContent>
            </Card>
            <div className="border-border bg-muted/30 rounded-md border px-4 py-3 text-sm">
                “{data.durable_copy_notice}”
            </div>
            <Card>
                <CardHeader>
                    <CardTitle>Durable workspace</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 text-sm sm:grid-cols-4">
                    <div>
                        <div className="text-muted-foreground">Generation</div>
                        <div className="font-medium">
                            {data.organization.generation ?? 'unknown'}
                        </div>
                    </div>
                    <div>
                        <div className="text-muted-foreground">
                            Durable bytes
                        </div>
                        <div className="font-medium">
                            {formatBytes(data.organization.durable_bytes)}
                        </div>
                    </div>
                    <div>
                        <div className="text-muted-foreground">Last used</div>
                        <div className="font-medium">
                            {data.organization.last_used_at
                                ? formatDistanceToNowStrict(
                                      new Date(data.organization.last_used_at),
                                      { addSuffix: true },
                                  )
                                : 'never'}
                        </div>
                    </div>
                    <div>
                        <div className="text-muted-foreground">Files</div>
                        <div className="font-medium">
                            {data.organization.durable_file_count}
                        </div>
                    </div>
                </CardContent>
            </Card>
            {data.runs.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                    No durable files found for this organization.
                </p>
            ) : (
                data.runs.map((run) => (
                    <Card key={`${run.run_id}-${run.mode}`}>
                        <CardHeader>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <CardTitle>
                                        {run.title || 'Untitled run'}
                                    </CardTitle>
                                    <p className="text-muted-foreground mt-1 font-mono text-xs">
                                        {run.run_id}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline">{run.mode}</Badge>
                                    {run.status ? (
                                        <Badge variant="secondary">
                                            {run.status}
                                        </Badge>
                                    ) : null}
                                    <a
                                        className="text-sm underline-offset-4 hover:underline"
                                        href={run.trace_url}
                                    >
                                        View trace
                                    </a>
                                </div>
                            </div>
                            <p className="text-muted-foreground text-xs">
                                Created {formatDate(run.created_at)} · last
                                activity {formatDate(run.last_activity_at)}
                            </p>
                            <p className="text-muted-foreground text-xs">
                                Durable bytes {formatBytes(run.durable_bytes)} /{' '}
                                {formatBytes(run.durable_cap_bytes)}
                            </p>
                        </CardHeader>
                        <CardContent>
                            {run.files_truncated ? (
                                <p className="text-muted-foreground mb-3 text-xs">
                                    Showing the first {data.max_files_per_run}{' '}
                                    files for this run.
                                </p>
                            ) : null}
                            <div className="divide-border divide-y rounded-md border">
                                {run.files.map((file) => (
                                    <div
                                        className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 text-sm"
                                        key={file.id}
                                    >
                                        <div className="min-w-0">
                                            <div className="truncate font-mono text-xs">
                                                {file.path}
                                            </div>
                                            <div className="text-muted-foreground text-xs">
                                                {formatBytes(file.size_bytes)} ·{' '}
                                                {formatDate(file.written_at)}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button
                                                className="text-sm underline-offset-4 hover:underline"
                                                type="button"
                                                onClick={() =>
                                                    setSelectedFile({
                                                        runId: run.run_id,
                                                        path: file.path,
                                                    })
                                                }
                                            >
                                                Preview
                                            </button>
                                            <a
                                                className="text-sm underline-offset-4 hover:underline"
                                                href={`${apiUrl}${file.download_url}`}
                                                download={true}
                                            >
                                                Download
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                ))
            )}
            {selectedFile ? (
                <Card>
                    <CardHeader>
                        <CardTitle>File preview</CardTitle>
                        <p className="text-muted-foreground font-mono text-xs">
                            {selectedFile.path}
                        </p>
                    </CardHeader>
                    <CardContent>
                        {isPreviewLoading ? (
                            <p className="text-muted-foreground text-sm">
                                Loading preview…
                            </p>
                        ) : preview ? (
                            <Fragment>
                                <pre className="bg-muted max-h-[32rem] overflow-auto rounded-md p-4 text-xs whitespace-pre-wrap">
                                    {preview.content}
                                </pre>
                                {preview.truncated ? (
                                    <p className="text-muted-foreground mt-2 text-xs">
                                        Preview capped at{' '}
                                        {formatBytes(preview.preview_max_bytes)}
                                        . Download the durable copy for the full
                                        file.
                                    </p>
                                ) : null}
                            </Fragment>
                        ) : (
                            <p className="text-muted-foreground text-sm">
                                Preview unavailable.
                            </p>
                        )}
                    </CardContent>
                </Card>
            ) : null}
            {data.runs_truncated ? (
                <p className="text-muted-foreground text-xs">
                    Showing the first {data.max_runs_per_response} runs. More
                    runs exist for this organization.
                </p>
            ) : null}
        </div>
    );
}
